/**
 * Demo Patient Detail View
 *
 * Uses the actual PatientDetailScreen component but with simulated demo data.
 * Accessed via /demo-patients/:id route.
 */

import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Container } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PatientDetailScreen from '~/components/patient/PatientDetailScreen';
import { getDemoState } from '~/services/demo/demoStateManager';
import { PatientDetailScreenQuery, VisitEventStatusEnum } from '~/graphql/__generated__/graphql';

export default function DemoPatientDetailView() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const parsedPatientId = useMemo(() => parseInt(patientId || ''), [patientId]);

  // Get demo state from localStorage
  const demoState = getDemoState();

  // Debug logging
  console.log('%c[DEMO PATIENT VIEW] Loading patient detail', 'color: #2196f3; font-weight: bold;');
  console.log('Patient ID from URL:', parsedPatientId);
  console.log('Demo state:', demoState);
  console.log('Scenario 1 patient ID:', demoState.scenario1?.patientId);
  console.log('Scenario 2 patient ID:', demoState.scenario2?.patientId);
  console.log('Scenario 3 patient ID:', demoState.scenario3?.patientId);

  // Find the scenario that matches this patient ID
  const scenario = [demoState.scenario1, demoState.scenario2, demoState.scenario3].find(
    (s) => s?.patientId === parsedPatientId
  );

  if (!scenario || !scenario.patientDetails || !scenario.episodeDetails) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back to Demo
        </Button>
        <Alert severity="error">
          Demo patient not found. Please execute a scenario first.
        </Alert>
      </Container>
    );
  }

  const { patientDetails, episodeDetails, visits } = scenario;

  // Transform mock data to match PatientDetailScreenQuery format
  const mockPatient: PatientDetailScreenQuery['patientSingle'] = {
    __typename: 'PatientSelectItem',
    id: patientDetails.id,
    firstName: patientDetails.firstName,
    lastName: patientDetails.lastName,
    phone: patientDetails.phone,
    preferredLanguage: null,
    sex: null,
    addressStreet: patientDetails.addressStreet,
    addressLineTwo: null,
    addressCity: patientDetails.addressCity,
    addressState: patientDetails.addressState,
    addressZip: patientDetails.addressZip,
    addressCoordinates: null,
    addressTimezone: 'America/New_York',
    dateOfBirth: patientDetails.dateOfBirth,
    patientNotes: 'Demo patient - simulated data only',
    serviceInstructions: null,
    patientIdentifiers: [
      {
        __typename: 'PatientPatientIdentifiersRelation',
        identifier: patientDetails.mrn,
        identifier_type: 'MRN',
      },
    ],
    episodes: [
      {
        __typename: 'PatientEpisodesRelation',
        id: episodeDetails.id,
        active: true,
        serviceLineId: 1,
        organizationalUnitId: null,
        updatedAt: episodeDetails.admissionDate,
        precautions: [],
      },
    ],
    careTeamProviders: [],
  };

  // Transform visits
  const mockVisits: PatientDetailScreenQuery['visit'] =
    visits?.map((visit) => ({
      __typename: 'VisitSelectItem',
      id: visit.id,
      startTime: visit.startTime,
      endTime: visit.endTime,
      eventStatus: visit.eventStatus as VisitEventStatusEnum,
      addressStreet: patientDetails.addressStreet,
      addressLineTwo: null,
      addressCity: patientDetails.addressCity,
      addressState: patientDetails.addressState,
      addressZip: patientDetails.addressZip,
      addressCoordinates: '',
      addressTimezone: 'America/New_York',
      serviceInstructions: null,
      arrivalWindowStartTime: visit.startTime,
      arrivalWindowEndTime: visit.endTime,
      episode: {
        __typename: 'VisitEpisodeRelation',
        serviceLineId: 1,
      },
      visitResources: visit.providers.map((provider) => ({
        __typename: 'VisitVisitResourcesRelation',
        providerId: provider.id,
        provider: {
          __typename: 'VisitVisitResourcesRelationProviderRelation',
          id: provider.id,
          firstName: provider.firstName,
          lastName: provider.lastName,
        },
      })),
      visitType: {
        __typename: 'VisitVisitTypeRelation',
        id: visit.visitType.id,
        name: visit.visitType.name,
      },
      visitServices: visit.services.map((service, idx) => ({
        __typename: 'VisitVisitServicesRelation',
        id: visit.id * 100 + idx,
        serviceId: service.id,
        service: {
          __typename: 'VisitVisitServicesRelationServiceRelation',
          id: service.id,
          name: service.name,
        },
      })),
      cancellationReason: null,
    })) || [];

  return (
    <Box>
      {/* Demo Banner */}
      <Box
        sx={{
          bgcolor: 'info.main',
          color: 'white',
          py: 1,
          px: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <strong>DEMO MODE:</strong> This is simulated demo data. No real database records.
        </Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ color: 'white' }}
        >
          Back to Demo
        </Button>
      </Box>

      {/* Use actual PatientDetailScreen component */}
      <PatientDetailScreen
        patient={mockPatient}
        visits={mockVisits}
        todaysVisits={[]}
        loading={false}
        refetch={() => {
          console.log('[DEMO] Refetch called - no action in demo mode');
        }}
      />
    </Box>
  );
}
